import mixpanel from 'mixpanel-browser';
import { getUserDetailsFromCookie } from './getCookies';

// REPLACE 'YOUR_MIXPANEL_TOKEN' WITH YOUR ACTUAL MIXPANEL PROJECT TOKEN
const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN || 'YOUR_MIXPANEL_TOKEN';

let mixpanelInitialized = false;

try {
  if (MIXPANEL_TOKEN && MIXPANEL_TOKEN !== 'YOUR_MIXPANEL_TOKEN') {
    mixpanel.init(MIXPANEL_TOKEN, {
      debug: process.env.NODE_ENV !== 'production', // Enable debug mode in development
      track_pageview: true, // Automatically track page views
      persistence: 'localStorage',
    });
    mixpanelInitialized = true;
    console.log('Mixpanel initialized');
  } else {
    console.warn('Mixpanel token is not set. Tracking will be disabled.');
  }
} catch (error) {
  console.error('Error initializing Mixpanel:', error);
}

/**
 * Helper function to get user context for events
 */
const getUserContext = () => {
  try {
    const user = getUserDetailsFromCookie();
    return user ? {
      user_id: user.id,
      user_email: user.email,
      subscription_type: user.subscription_type || 'unknown'
    } : {};
  } catch (error) {
    console.warn('Could not get user context:', error);
    return {};
  }
};

/**
 * Tracks an event with Mixpanel.
 * @param {string} eventName - The name of the event.
 * @param {object} properties - Additional properties for the event.
 */
export const trackEvent = (eventName, properties = {}) => {
  if (mixpanelInitialized) {
    try {
      const userContext = getUserContext();
      mixpanel.track(eventName, {
        ...userContext,
        ...properties,
        'URL': window.location.href,
        'Path': window.location.pathname,
        'Timestamp': new Date().toISOString(),
        // Add any other global properties you want for every event
      });
      console.log(`Event tracked: ${eventName}`, properties);
    } catch (error) {
      console.error(`Error tracking event [${eventName}]:`, error);
    }
  } else {
    console.log(`Mixpanel not initialized. Event not tracked: ${eventName}`, properties);
  }
};

/**
 * Safe wrapper for tracking events with error handling
 */
export const safeTrackEvent = (eventName, properties = {}) => {
  try {
    if (!mixpanelInitialized) {
      console.warn(`Mixpanel not initialized. Event not tracked: ${eventName}`);
      return;
    }
    
    trackEvent(eventName, properties);
  } catch (error) {
    console.error(`Error tracking ${eventName}:`, error);
  }
};

/**
 * Identifies the user in Mixpanel.
 * Call this when the user logs in or their ID becomes known.
 * @param {string} userId - The unique identifier for the user.
 * @param {object} traits - Optional properties to set on the user's profile (e.g., email, name).
 */
export const identifyUser = (userId, traits = {}) => {
  if (mixpanelInitialized && userId) {
    try {
      mixpanel.identify(userId);
      if (Object.keys(traits).length > 0) {
        mixpanel.people.set(traits);
      }
      console.log(`User identified: ${userId}`, traits);
    } catch (error) {
      console.error('Error identifying user:', error);
    }
  }
};

/**
 * Registers super properties that will be sent with every event.
 * @param {object} properties - Properties to register.
 */
export const registerSuperProperties = (properties = {}) => {
  if (mixpanelInitialized) {
    try {
      mixpanel.register(properties);
    } catch (error) {
      console.error('Error registering super properties:', error);
    }
  }
};

/**
 * Sets properties on the current user's Mixpanel profile.
 * (Requires mixpanel.identify to have been called).
 * @param {object} properties - Properties to set.
 */
export const setPeopleProperties = (properties = {}) => {
  if (mixpanelInitialized) {
    try {
      mixpanel.people.set(properties);
    } catch (error) {
      console.error('Error setting people properties:', error);
    }
  }
};

/**
 * Resets Mixpanel identification, for example when a user logs out.
 */
export const resetMixpanel = () => {
  if (mixpanelInitialized) {
    try {
      mixpanel.reset();
    } catch (error) {
      console.error('Error resetting Mixpanel:', error);
    }
  }
};

// ============================================================================
// EDUCATION-SPECIFIC TRACKING FUNCTIONS
// ============================================================================

/**
 * Tracks when a user achieves a badge
 * @param {object} badgeData - Badge information
 */
export const trackBadgeAchieved = (badgeData) => {
  safeTrackEvent('Badge Achieved', {
    badge_id: badgeData.id,
    badge_name: badgeData.name,
    video_track_id: badgeData.videoTrackId,
    achievement_date: new Date().toISOString(),
    badge_type: badgeData.type || 'unknown'
  });
};

/**
 * Tracks video engagement events (play, pause, complete, etc.)
 * @param {object} videoData - Video engagement data
 */
export const trackVideoEngagement = (videoData) => {
  safeTrackEvent('Video Engagement', {
    video_id: videoData.videoId,
    video_title: videoData.videoTitle || 'Unknown',
    trail_id: videoData.trailId,
    trail_name: videoData.trailName || 'Unknown',
    module_id: videoData.moduleId,
    module_name: videoData.moduleName || 'Unknown',
    action: videoData.action, // play, pause, resume, complete, seek, progress
    progress_percent: Math.round(videoData.progressPercent || 0),
    time_watched: Math.round(videoData.timeWatched || 0),
    total_duration: Math.round(videoData.totalDuration || 0),
    session_duration: Math.round(videoData.sessionDuration || 0),
    pause_count: videoData.pauseCount || 0,
    playback_rate: videoData.playbackRate || 1,
    timestamp: new Date().toISOString()
  });
};

/**
 * Tracks trail progress and completion
 * @param {object} trailData - Trail progress data
 */
export const trackTrailProgress = (trailData) => {
  safeTrackEvent('Trail Progress', {
    trail_id: trailData.trailId,
    trail_name: trailData.trailName,
    trail_category: trailData.trailCategory || 'Unknown',
    progress_percent: Math.round(trailData.progressPercent || 0),
    videos_completed: trailData.videosCompleted || 0,
    total_videos: trailData.totalVideos || 0,
    modules_completed: trailData.modulesCompleted || 0,
    total_modules: trailData.totalModules || 0,
    time_spent: Math.round(trailData.timeSpent || 0),
    last_video_watched: trailData.lastVideoWatched || 'Unknown',
    timestamp: new Date().toISOString()
  });
};

/**
 * Tracks module completion
 * @param {object} moduleData - Module completion data
 */
export const trackModuleCompletion = (moduleData) => {
  safeTrackEvent('Module Completed', {
    module_id: moduleData.moduleId,
    module_name: moduleData.moduleName,
    trail_id: moduleData.trailId,
    trail_name: moduleData.trailName,
    completion_time: moduleData.completionTime || Date.now(),
    videos_watched: moduleData.videosWatched || 0,
    total_videos: moduleData.totalVideos || 0,
    average_video_completion: Math.round(moduleData.averageVideoCompletion || 0),
    timestamp: new Date().toISOString()
  });
};

/**
 * Tracks trail start event
 * @param {object} trailData - Trail start data
 */
export const trackTrailStarted = (trailData) => {
  safeTrackEvent('Trail Started', {
    trail_id: trailData.trailId,
    trail_name: trailData.trailName,
    trail_category: trailData.trailCategory || 'Unknown',
    total_modules: trailData.totalModules || 0,
    total_videos: trailData.totalVideos || 0,
    estimated_duration: trailData.estimatedDuration || 0,
    difficulty_level: trailData.difficultyLevel || 'Unknown',
    timestamp: new Date().toISOString()
  });
};

/**
 * Tracks page view events (replacement for Umami)
 * @param {object} pageData - Page view data
 */
export const trackPageView = (pageData) => {
  safeTrackEvent('Page View', {
    page_name: pageData.pageName,
    page_title: pageData.pageTitle || document.title,
    page_url: pageData.pageUrl || window.location.pathname,
    referrer: document.referrer || 'Direct',
    timestamp: new Date().toISOString()
  });
};

/**
 * Tracks user interactions (clicks, form submissions, etc.)
 * @param {object} interactionData - Interaction data
 */
export const trackUserInteraction = (interactionData) => {
  safeTrackEvent('User Interaction', {
    interaction_type: interactionData.type, // click, form_submit, download, etc.
    element_id: interactionData.elementId,
    element_text: interactionData.elementText,
    page_name: interactionData.pageName,
    timestamp: new Date().toISOString()
  });
};

/**
 * Tracks payment-related events
 * @param {object} paymentData - Payment event data
 */
export const trackPaymentEvent = (paymentData) => {
  safeTrackEvent('Payment Event', {
    event_type: paymentData.eventType, // attempt, success, failure
    payment_method: paymentData.paymentMethod,
    amount: paymentData.amount,
    currency: paymentData.currency || 'BRL',
    plan_type: paymentData.planType,
    timestamp: new Date().toISOString()
  });
};

/**
 * Tracks video pause events
 * @param {object} data - Pause event data
 */
export const trackVideoPaused = (data) => {
  safeTrackEvent('Video Paused', {
    video_id: data.videoId,
    video_title: data.videoTitle || 'Unknown',
    trail_id: data.trailId,
    trail_name: data.trailName || 'Unknown',
    module_id: data.moduleId,
    module_name: data.moduleName || 'Unknown',
    paused_at_percent: Math.round(data.pausedAtPercent || 0),
    paused_at_seconds: Math.round(data.pausedAtSeconds || 0),
    total_duration: Math.round(data.totalDuration || 0),
    pause_count: data.pauseCount || 0,
    playback_rate: data.playbackRate || 1,
    timestamp: new Date().toISOString()
  });
};

/**
 * Tracks video seek events
 * @param {object} data - Seek event data
 */
export const trackVideoSeeked = (data) => {
  safeTrackEvent('Video Seeked', {
    video_id: data.videoId,
    video_title: data.videoTitle || 'Unknown',
    trail_id: data.trailId,
    trail_name: data.trailName || 'Unknown',
    module_id: data.moduleId,
    module_name: data.moduleName || 'Unknown',
    seek_from_seconds: Math.round(data.seekFromSeconds || 0),
    seek_to_seconds: Math.round(data.seekToSeconds || 0),
    seek_direction: data.seekDirection || 'unknown',
    total_duration: Math.round(data.totalDuration || 0),
    timestamp: new Date().toISOString()
  });
};

/**
 * Tracks playback rate changes
 * @param {object} data - Rate change data
 */
export const trackPlaybackRateChanged = (data) => {
  safeTrackEvent('Playback Rate Changed', {
    video_id: data.videoId,
    video_title: data.videoTitle || 'Unknown',
    trail_id: data.trailId,
    trail_name: data.trailName || 'Unknown',
    module_id: data.moduleId,
    module_name: data.moduleName || 'Unknown',
    previous_rate: data.previousRate || 1,
    new_rate: data.newRate || 1,
    changed_at_percent: Math.round(data.changedAtPercent || 0),
    timestamp: new Date().toISOString()
  });
};

/**
 * Tracks video replay (play after ended)
 * @param {object} data - Replay data
 */
export const trackVideoReplay = (data) => {
  safeTrackEvent('Video Replay', {
    video_id: data.videoId,
    video_title: data.videoTitle || 'Unknown',
    trail_id: data.trailId,
    trail_name: data.trailName || 'Unknown',
    module_id: data.moduleId,
    module_name: data.moduleName || 'Unknown',
    replay_count: data.replayCount || 1,
    timestamp: new Date().toISOString()
  });
};

/**
 * Tracks when a module is started (first video of a module)
 * @param {object} data - Module start data
 */
export const trackModuleStarted = (data) => {
  safeTrackEvent('Module Started', {
    module_id: data.moduleId,
    module_name: data.moduleName || 'Unknown',
    trail_id: data.trailId,
    trail_name: data.trailName || 'Unknown',
    total_videos: data.totalVideos || 0,
    timestamp: new Date().toISOString()
  });
};

export default mixpanel;
